import { createSlice } from "@reduxjs/toolkit";
import { IHomeInitialState } from "@/types/home.types";
import {
  getAllActiveBanner,
  getAllActiveSectors,
  getAllActiveSettings,
  getAllActiveTestimonials,
  getAllCMSPageData,
  getAllMetaData,
  getGeneralPagesDetails,
  getHomeDetails,
  getHeaderMenuData,
} from "@/services/home.services";

const initialState: IHomeInitialState = {
  loading: false,
  bannerLoading: false,
  settingLoading: false,
  seoLoading: false,
  testimonialLoading: false,
  sectorLoading: false,
  homeLoading: false,
  error: "",
  homeDetails: {},
  PageDetails: [],
  MetaDetails: {
    MetaTitle: "",
    MetaKeywords: "",
    MetaDescription: "",
  },
  SettingList: [],
  TestominalList: [],
  SectorList: [],
  BannerList: [],
  headerMenuLoading: false,
  HeaderMenuList: null,
};

const homeSlice = createSlice({
  name: "home",
  initialState,
  reducers: {
    setSettings: (state, action) => {
      state.SettingList = action.payload;
    },
    setBanners: (state, action) => {
      state.BannerList = action.payload;
    },
    setHomeDetails: (state, action) => {
      state.homeDetails = action.payload;
      if (action.payload?.Banner) {
        state.BannerList = action.payload.Banner;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getHomeDetails.pending, (state) => {
      state.homeLoading = true;
    });
    builder.addCase(getHomeDetails.fulfilled, (state, action) => {
      state.homeLoading = false;
      state.homeDetails = action.payload;
      if (action.payload?.Banner) {
        state.BannerList = action.payload.Banner;
      }
      state.error = "";
    });
    builder.addCase(getHomeDetails.rejected, (state, action) => {
      state.homeLoading = false;
      state.homeDetails = initialState.homeDetails;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(getGeneralPagesDetails.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getGeneralPagesDetails.fulfilled, (state, action) => {
      state.loading = false;
      state.PageDetails = action.payload;
      state.error = "";
    });
    builder.addCase(getGeneralPagesDetails.rejected, (state, action) => {
      state.loading = false;
      state.PageDetails = initialState.PageDetails;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(getAllActiveSettings.pending, (state) => {
      state.settingLoading = true;
    });
    builder.addCase(getAllActiveSettings.fulfilled, (state, action) => {
      state.settingLoading = false;
      state.SettingList = action.payload;
      state.error = "";
    });
    builder.addCase(getAllActiveSettings.rejected, (state, action) => {
      state.settingLoading = false;
      state.SettingList = initialState.SettingList;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(getAllMetaData.pending, (state) => {
      state.seoLoading = true;
    });
    builder.addCase(getAllMetaData.fulfilled, (state, action) => {
      state.seoLoading = false;
      state.MetaDetails = action.payload;
      state.error = "";
    });
    builder.addCase(getAllMetaData.rejected, (state, action) => {
      state.seoLoading = false;
      state.MetaDetails = initialState.MetaDetails;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(getAllActiveTestimonials.pending, (state) => {
      state.testimonialLoading = true;
    });
    builder.addCase(getAllActiveTestimonials.fulfilled, (state, action) => {
      state.testimonialLoading = false;
      state.TestominalList = action.payload || [];
      state.error = "";
    });
    builder.addCase(getAllActiveTestimonials.rejected, (state, action) => {
      state.testimonialLoading = false;
      state.TestominalList = [];
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(getAllActiveSectors.pending, (state) => {
      state.sectorLoading = true;
    });
    builder.addCase(getAllActiveSectors.fulfilled, (state, action) => {
      state.sectorLoading = false;
      state.SectorList = action.payload || [];
      state.error = "";
    });
    builder.addCase(getAllActiveSectors.rejected, (state, action) => {
      state.sectorLoading = false;
      state.SectorList = [];
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(getAllCMSPageData.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAllCMSPageData.fulfilled, (state, action) => {
      state.loading = false;
      state.PageDetails = action.payload || [];
      state.error = "";
    });
    builder.addCase(getAllCMSPageData.rejected, (state, action) => {
      state.loading = false;
      state.PageDetails = [];
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(getAllActiveBanner.pending, (state) => {
      state.bannerLoading = true;
    });
    builder.addCase(getAllActiveBanner.fulfilled, (state, action) => {
      state.bannerLoading = false;
      state.BannerList = action.payload || [];
      state.error = "";
    });
    builder.addCase(getAllActiveBanner.rejected, (state, action) => {
      state.bannerLoading = false;
      state.BannerList = [];
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(getHeaderMenuData.pending, (state) => {
      state.headerMenuLoading = true;
    });
    builder.addCase(getHeaderMenuData.fulfilled, (state, action) => {
      state.headerMenuLoading = false;
      state.HeaderMenuList = action.payload || null;
      state.error = "";
    });
    builder.addCase(getHeaderMenuData.rejected, (state, action) => {
      state.headerMenuLoading = false;
      state.HeaderMenuList = null;
      state.error = action.error.message || "Something went wrong";
    });
  },
});

export const { setSettings, setBanners, setHomeDetails } = homeSlice.actions;
export default homeSlice.reducer;
